import { MASTER_DEFINITIONS } from '@/features/masters';
import type { MatrixGroup, MatrixItem, PermissionMatrix } from './rbac.types';

/**
 * The names the matrix draws, where the menu calls a feature something other than its `os_feature` row.
 *
 * <p>The platform answers each item by its feature name, and that name is what every guard and route
 * resolves -- so it is never renamed in the table. The sidebar, though, has its own labels for a few of them,
 * and an administrator ticks permissions against the names they navigate by. So the matrix draws the menu's
 * label and keeps the feature name beside it (`MatrixItem.feature`).
 *
 * <p>Display only: the save sends feature ids, never names, so nothing written depends on this.
 */

/**
 * Templates whose menu label differs from the feature. The Templates menu has called the RFQ template
 * "BID Template" since the screens were renamed; the feature -- and the guards naming it -- stayed
 * `RFQ Template`.
 */
export const TEMPLATE_LABELS: Readonly<Record<string, string>> = {
  'RFQ Template': 'BID Template',
};

/**
 * Functions whose menu label differs from the feature. The sidebar has called the RFQ screens "BIDs" since they were
 * renamed; the feature -- and the guards naming it -- stayed `Request For Quotation` (2026-10-27).
 */
export const FUNCTION_LABELS: Readonly<Record<string, string>> = {
  'Request For Quotation': 'BIDs',
};

/** Masters take the Masters menu's own labels, read from the registry the menu agrees with. */
const MASTER_LABELS = new Map<string, string>(
  MASTER_DEFINITIONS.map((definition) => [definition.feature.toLowerCase(), definition.label]),
);

const TEMPLATE_LABELS_LOWER = new Map<string, string>(
  Object.entries(TEMPLATE_LABELS).map(([feature, label]) => [feature.toLowerCase(), label]),
);

const FUNCTION_LABELS_LOWER = new Map<string, string>(
  Object.entries(FUNCTION_LABELS).map(([feature, label]) => [feature.toLowerCase(), label]),
);

/** The label a feature is drawn under in a group, or undefined where it is drawn as it is named. */
export function displayLabelOf(groupKey: string, feature: string): string | undefined {
  const key = feature.trim().toLowerCase();
  if (groupKey === 'FUNCTION') return FUNCTION_LABELS_LOWER.get(key);
  if (groupKey === 'TEMPLATE') return TEMPLATE_LABELS_LOWER.get(key);
  if (groupKey === 'MASTER') return MASTER_LABELS.get(key);
  return undefined;
}

function relabel(groupKey: string, item: MatrixItem): MatrixItem {
  const label = displayLabelOf(groupKey, item.name);
  if (label === undefined || label === item.name) return item;
  return { ...item, name: label, feature: item.feature ?? item.name };
}

function relabelled(group: MatrixGroup): MatrixGroup {
  return { ...group, items: group.items.map((item) => relabel(group.key, item)) };
}

/** The matrix with every item drawn under its menu label. Applied after `inMastersMenuOrder`, which matches on feature names. */
export function withDisplayLabels(matrix: PermissionMatrix): PermissionMatrix {
  return { ...matrix, groups: matrix.groups.map(relabelled) };
}
